/**
 * EditorContextChip — shows the active file + optional selection info.
 * Always visible when a tab is open; selection detail appended when text is selected.
 * Hover tooltip shows file path, selection detail, and text preview.
 *
 * Supports all selection kinds:
 *   lines   — text editor with line numbers
 *   cells   — spreadsheet with cell range (A1:C5)
 *   slide   — presentation/drawing with slide number
 *   text    — generic text selection (no position info)
 *   element — HTML preview design-mode element (selector + DOM path)
 */
import { X, Eye, EyeOff } from 'lucide-react'
import type { EditorSelection } from '@papan/collab/client'
import { Tooltip } from '@papan/core-ui/ui'

export interface ActiveTabInfo {
  fileId: string
  fileName: string
  filePath: string
  fileType: string
}

interface EditorContextChipProps {
  activeTab: ActiveTabInfo
  selection?: EditorSelection | null
  /** Dismiss affordance (X) — used by ChatPanel where the chip is transient. */
  onDismiss?: () => void
  /** Toggle mode (chat-home hero): the whole chip is a button that flips
   *  `active`. When provided, the chip stays visible off/on instead of being
   *  dismissed, and `onDismiss` is ignored. */
  active?: boolean
  onToggle?: () => void
  /** The file has unsaved editor edits — surfaced as "(unsaved)" in the tooltip. */
  unsaved?: boolean
}

function getSelectionLabel(sel: EditorSelection): string {
  switch (sel.kind) {
    case 'element':
      return sel.elementSelector || (sel.elementTag ? `<${sel.elementTag}>` : 'element selected')
    case 'lines': {
      const count = Math.abs(sel.endLine - sel.startLine) + 1
      return count === 1 ? '1 line selected' : `${count} lines selected`
    }
    case 'cells':
      return sel.cellRange || 'cells selected'
    case 'slide': {
      const slide = sel.slideNumber ? `Slide ${sel.slideNumber}` : 'slide'
      return sel.text ? `${slide} · selected` : slide
    }
    case 'text':
    default: {
      const lines = sel.text.split('\n').length
      const prefix = sel.pageNumber ? `Page ${sel.pageNumber} · ` : ''
      if (lines > 1) return `${prefix}${lines} lines selected`
      const chars = sel.text.length
      return `${prefix}${chars === 1 ? '1 char selected' : `${chars} chars selected`}`
    }
  }
}

function getTooltipDetail(sel: EditorSelection): string | null {
  switch (sel.kind) {
    case 'element': {
      const lines = sel.startLine > 0
        ? (sel.startLine === sel.endLine ? `L${sel.startLine}` : `L${sel.startLine}–${sel.endLine}`)
        : null
      if (sel.elementPath && lines) return `${sel.elementPath} · ${lines}`
      return sel.elementPath || lines
    }
    case 'lines':
      return sel.startLine === sel.endLine
        ? `Line ${sel.startLine}`
        : `Lines ${sel.startLine}–${sel.endLine}`
    case 'cells': {
      const sheet = sel.sheetName ? `${sel.sheetName} · ` : ''
      return sel.cellRange ? `${sheet}${sel.cellRange}` : null
    }
    case 'slide':
      return sel.slideNumber ? `Slide ${sel.slideNumber}` : null
    case 'text':
    default:
      return sel.pageNumber ? `Page ${sel.pageNumber}` : null
  }
}

export function EditorContextChip({ activeTab, selection, onDismiss, active, onToggle, unsaved }: EditorContextChipProps) {
  const label = selection ? getSelectionLabel(selection) : activeTab.fileName
  const tooltipDetail = selection ? getTooltipDetail(selection) : null

  // Element selections carry their OWN file identity — on a multi-page app
  // the user may have navigated the preview to page2.html while the tab
  // (activeTab) is still the entry file. Show the file the element actually
  // came from, not the tab, so the chip never misattributes the context.
  const isElement = selection?.kind === 'element'
  const tooltipFileName = isElement && selection.fileName ? selection.fileName : activeTab.fileName
  const tooltipFilePath = isElement && selection.filePath ? selection.filePath : activeTab.filePath

  const preview = selection ? selection.text.split('\n').slice(0, 3).join('\n') : null
  const truncated = preview != null && selection != null && preview.length < selection.text.length

  // Toggle mode (onToggle) vs dismiss mode (onDismiss). In toggle mode the
  // whole chip is a button and `active` drives the on/off look; off = dimmed
  // with an EyeOff icon, signalling the file is present but excluded.
  const isToggle = !!onToggle
  const isActive = active ?? true
  const Icon = isToggle && !isActive ? EyeOff : Eye
  // A grid, not a flex row: the minmax(0,1fr) label track counts as zero in the
  // chip's min-content width, so a squeezed chip truncates the label down to
  // icon + X and stops there. The old `inline-flex min-w-0` let the chip shrink
  // past its own icons, and the X spilled outside the border.
  const gridCols = !isToggle && onDismiss ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)]'
  const chipClass = `inline-grid ${gridCols} items-center gap-1 rounded-md border px-1.5 py-0.5 shrink transition-colors ${
    isToggle
      ? (isActive
          ? 'border-blue-500/30 bg-blue-500/10 cursor-pointer hover:bg-blue-500/15'
          : 'themed-border bg-blue-500/5 opacity-60 cursor-pointer hover:opacity-85')
      : 'border-blue-500/30 bg-blue-500/10 cursor-default'
  }`
  const iconClass = `${isToggle && !isActive ? 'themed-text-muted' : 'text-blue-400'} shrink-0`

  const tip = (
    <div className="max-w-[300px]">
      <div className="font-medium text-zinc-200">
        {tooltipFileName}{tooltipDetail ? ` · ${tooltipDetail}` : ''}{unsaved ? ' (unsaved)' : ''}
      </div>
      <div className="text-zinc-400 text-[10px] mt-0.5 truncate">{tooltipFilePath}</div>
      {isToggle && (
        <div className="text-zinc-500 text-[10px] mt-2">
          {isActive ? 'Included as context · click to exclude' : 'Excluded · click to include'}
        </div>
      )}
      {preview && (
        <pre className="mt-1 px-1.5 py-1 text-[10px] text-zinc-300 whitespace-pre-wrap break-words leading-relaxed max-h-[60px] overflow-hidden border border-zinc-700 rounded bg-zinc-800/50">
          {preview}{truncated ? '…' : ''}
        </pre>
      )}
    </div>
  )

  return (
    <Tooltip always placement="top" className="inline-flex shrink max-w-full" content={tip}>
      {isToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={isActive}
          className={chipClass}
        >
          <Icon size={10} className={iconClass} />
          <span className="text-[11px] themed-text whitespace-nowrap truncate">
            {label}
          </span>
        </button>
      ) : (
        <div className={chipClass}>
          <Eye size={10} className="text-blue-400 shrink-0" />
          <span className="text-[11px] themed-text whitespace-nowrap truncate">
            {label}
          </span>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="themed-text-muted hover:themed-text transition-colors shrink-0"
            >
              <X size={8} />
            </button>
          )}
        </div>
      )}
    </Tooltip>
  )
}
